import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  FormControlLabel,
  FormGroup,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useApi } from '../contexts/ApiContext';

export type AgentPlugin = 'enshrouded' | 'valheim' | 'minecraft';

interface AgentStatus {
  agent_enabled: boolean;
  plugin: AgentPlugin | null;
  idle_shutdown_enabled: boolean;
  idle_shutdown_minutes: number;
  cloud_stop_allowed: boolean;
  /** Default true when unset in Dynamo (opt out). */
  discord_notify_web_actions?: boolean;
  discord_notify_idle_shutdown?: boolean;
  agent_registered: boolean;
  agent_last_seen: number | null;
  agent_last_state: Record<string, unknown> | null;
}

interface AgentPanelProps {
  guildId: string;
  instanceNickname: string;
}

const formatRelative = (epochSeconds: number | null): string => {
  if (!epochSeconds) return 'never';
  const delta = Math.max(0, Math.floor(Date.now() / 1000 - epochSeconds));
  if (delta < 60) return `${delta}s ago`;
  if (delta < 3600) return `${Math.floor(delta / 60)}m ago`;
  if (delta < 86400) return `${Math.floor(delta / 3600)}h ago`;
  return `${Math.floor(delta / 86400)}d ago`;
};

const DEFAULT_SAGA_AGENT_INSTALL =
  'https://saga-agent.rhynonorm.com/latest/install.sh';

/** Shown after install/restart: how to know registration succeeded. */
function AgentVerifySteps() {
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Confirm it works
      </Typography>
      <Typography variant="body2" color="text.secondary" component="div" sx={{ '& p': { mt: 0, mb: 0.75 } }}>
        <p>
          <strong>1. On the server (SSH / SSM)</strong> — <code>sudo systemctl is-active saga-agent</code> should
          print <code>active</code>. After a successful first-time register,{' '}
          <code>sudo test -f /etc/saga-agent/refresh.token</code> should succeed (the agent wrote the long-lived
          refresh token; mode <code>0600</code>).
        </p>
        <p>
          <strong>2. Logs (optional)</strong> — <code>sudo journalctl -u saga-agent -b -n 50 --no-pager</code>. On
          the <strong>first</strong> run that uses a one-shot token you should see <code>registering with control
          plane</code> in the JSON log; after <code>refresh.token</code> exists, restarts may skip that line and
          only refresh tokens. There should be no <code>bootstrap</code> error or <code>saga-agent exited with
          error</code>. <code>register: http 403</code> (or 404) usually means a bad or expired token, or
          nickname / guild not matching the Saga <strong>Register</strong> record. An <code>http 500</code> with
          <code>internal server error</code> (or <code>failed to persist agent registration</code>) is a
          control-plane failure — check CloudWatch logs for <code>/aws/lambda/saga-bot-agent-interactions</code>
          after redeploying the latest Lambda.
        </p>
        <p>
          <strong>3. Here in Saga</strong> — reload this page. Under Agent, <strong>Registered</strong> should
          be <strong>yes</strong> and <strong>Last seen</strong> a recent time (the agent sent a startup event).
        </p>
        <p>
          If you added <code>provisioning_token</code> in <code>config.yml</code> on a current agent, that line
          should disappear from the file after a successful register.
        </p>
      </Typography>
    </Box>
  );
}

interface ProvisioningTokenHelpProps {
  baseUrl: string;
  token: string;
  guildId: string;
  instanceNickname: string;
  /** Game plugin; defaults to `minecraft` if unset in the form. */
  plugin: string;
}

/** Copy-paste install / file-based steps shown whenever a new provisioning token is minted. */
function ProvisioningTokenHelp({
  baseUrl,
  token,
  guildId,
  instanceNickname,
  plugin,
}: ProvisioningTokenHelpProps) {
  const installScriptUrl =
    import.meta.env.VITE_SAGA_AGENT_INSTALL_SCRIPT_URL || DEFAULT_SAGA_AGENT_INSTALL;
  const installCmd = [
    `curl -fsSL ${JSON.stringify(installScriptUrl)} | sudo \\`,
    `  SAGA_API_URL=${JSON.stringify(baseUrl)} \\`,
    `  SAGA_PROVISIONING_TOKEN=${JSON.stringify(token)} \\`,
    `  SAGA_GUILD_ID=${JSON.stringify(guildId)} \\`,
    `  SAGA_INSTANCE_NICKNAME=${JSON.stringify(instanceNickname)} \\`,
    `  SAGA_PLUGIN=${JSON.stringify(plugin)} \\`,
    '  bash',
  ].join('\n');

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        On the game server, the one-shot goes in <code>/etc/saga-agent/config.yml</code>{' '}
        as <code>provisioning_token: &apos;…&apos;</code> (the agent drops that key after a
        successful register). Legacy: <code>/etc/saga-agent/provisioning.env</code> and{' '}
        <code>SAGA_PROVISIONING_TOKEN</code> in the process env; if both file and env are
        set, the env wins. The <code>curl | bash</code> snippet only bootstraps the same
        content. Replaced the VM? Update the instance on the Saga <strong>Register</strong>
        page, then re-run.
      </Typography>
      <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
        First install (on the game server, as root)
      </Typography>
      <Box
        component="pre"
        sx={{
          p: 1,
          bgcolor: 'action.hover',
          borderRadius: 1,
          overflowX: 'auto',
          fontFamily: 'monospace',
          fontSize: '0.75rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
        }}
      >
        {installCmd}
      </Box>
      <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }}>
        Agent already installed — add token in config, then restart
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Add this line to <code>/etc/saga-agent/config.yml</code> (or the env file
        below if you use the legacy path), then:
      </Typography>
      <Box
        component="pre"
        sx={{
          p: 1,
          bgcolor: 'action.hover',
          borderRadius: 1,
          overflowX: 'auto',
          fontFamily: 'monospace',
          fontSize: '0.75rem',
        }}
      >
        {`provisioning_token: '${token.replace(/'/g, "''")}'`}
        {'\n'}
        {`# sudo chmod 600 /etc/saga-agent/config.yml && sudo systemctl restart saga-agent`}
        {'\n'}
        {`# legacy: SAGA_PROVISIONING_TOKEN=${token}  →  /etc/saga-agent/provisioning.env`}
      </Box>
    </Box>
  );
}

export const AgentPanel: React.FC<AgentPanelProps> = ({ guildId, instanceNickname }: AgentPanelProps) => {
  const { get, post, baseUrl } = useApi();
  const [status, setStatus] = useState<AgentStatus | null>(null);
  // Initialize loading=true so we don't have to call setLoading(true)
  // synchronously at the start of the mount-fetch effect (which would
  // trip react-hooks/set-state-in-effect).
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const [provisioningToken, setProvisioningToken] = useState<string | null>(null);

  const query = `guild_id=${encodeURIComponent(guildId)}&instance_nickname=${encodeURIComponent(instanceNickname)}`;

  // `refresh` is used by the post-mutation button handlers below to
  // reload after a config change. It can setLoading(true) synchronously
  // because it runs inside an event handler, not inside an effect body.
  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data: AgentStatus = await get(`/instances/agent?${query}`);
      setStatus(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agent status');
    } finally {
      setLoading(false);
    }
  }, [get, query]);

  // Mount-fetch effect, inlined so every setState is inside the async
  // callback (not synchronously in the effect body). Calling `refresh`
  // directly here trips react-hooks/set-state-in-effect because
  // `refresh` starts with a synchronous setLoading(true); that's fine
  // inside event handlers but not inside effect bodies.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data: AgentStatus = await get(`/instances/agent?${query}`);
        if (cancelled) return;
        setStatus(data);
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load agent status');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [get, query]);

  const enqueue = async (action: 'start' | 'stop' | 'restart') => {
    setPending(true);
    setError(null);
    setInfo(null);
    try {
      await post('/instances/agent/command', {
        guild_id: guildId,
        instance_nickname: instanceNickname,
        action,
      });
      setInfo(`Queued ${action} command.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enqueue command');
    } finally {
      setPending(false);
    }
  };

  const mintProvisioning = async () => {
    setPending(true);
    setError(null);
    setInfo(null);
    setProvisioningToken(null);
    try {
      const res = await post('/instances/agent/provisioning-token', {
        guild_id: guildId,
        instance_nickname: instanceNickname,
      });
      setProvisioningToken(res.provisioning_token);
      // Minting a token also flips agent_enabled server-side; reload
      // status so the rest of the panel unlocks.
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mint token');
    } finally {
      setPending(false);
    }
  };

  const disableAgent = async () => {
    const confirmMsg =
      'Disable the agent for this instance? This deletes the SQS queue and revokes the ' +
      'current refresh token. The agent on the VM will lose access until you re-enable ' +
      'and re-install. Continue?';
    if (!window.confirm(confirmMsg)) return;
    setPending(true);
    setError(null);
    setInfo(null);
    setProvisioningToken(null);
    try {
      await post('/instances/agent/disable', {
        guild_id: guildId,
        instance_nickname: instanceNickname,
      });
      setInfo('Agent disabled. You can re-enable it at any time.');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable agent');
    } finally {
      setPending(false);
    }
  };

  const setDiscordNotifyFlag = async (
    key: 'discord_notify_web_actions' | 'discord_notify_idle_shutdown',
    value: boolean,
  ) => {
    setPending(true);
    setError(null);
    try {
      await post('/instances/agent/config', {
        guild_id: guildId,
        instance_nickname: instanceNickname,
        [key]: value,
      });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update notification settings');
    } finally {
      setPending(false);
    }
  };

  const revokeRefreshToken = async () => {
    const confirmMsg =
      'Revoke the agent\'s current refresh token? The agent will stop being able to ' +
      'mint new credentials; its existing access token + STS session will keep working ' +
      'for up to ~1 hour. The SQS queue and idle-shutdown config are preserved. To ' +
      'restore access, rotate the install token and re-install the agent. Continue?';
    if (!window.confirm(confirmMsg)) return;
    setPending(true);
    setError(null);
    setInfo(null);
    try {
      const res = await post('/instances/agent/revoke', {
        guild_id: guildId,
        instance_nickname: instanceNickname,
      });
      setInfo(
        res?.status === 'noop'
          ? 'No active refresh token to revoke.'
          : 'Refresh token revoked. The agent will lose refresh capability within ~1h.'
      );
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke refresh token');
    } finally {
      setPending(false);
    }
  };

  const lastState = status?.agent_last_state as
    | { event?: string; data?: Record<string, unknown> }
    | null;

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 1, mb: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Agent
      </Typography>

      {loading && !status && <Typography variant="body2">Loading agent status...</Typography>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}

      {status && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Discord channel notifications
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Optional posts to your allowed text channels (same IDs as slash-command allowlist).
            Turn off if you do not want Saga to announce these events in Discord.
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={status.discord_notify_web_actions !== false}
                  onChange={(e) => {
                    void setDiscordNotifyFlag('discord_notify_web_actions', e.target.checked);
                  }}
                  disabled={pending}
                />
              }
              label="Web UI: cloud start / stop"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={status.discord_notify_idle_shutdown !== false}
                  onChange={(e) => {
                    void setDiscordNotifyFlag('discord_notify_idle_shutdown', e.target.checked);
                  }}
                  disabled={pending}
                />
              }
              label="Idle shutdown (Saga stops the instance after empty server timeout)"
            />
          </FormGroup>
        </Box>
      )}

      {status && !status.agent_enabled && (
        <Stack spacing={2}>
          <Alert severity="info">
            The saga-agent is <strong>not enabled</strong> for this instance. Enabling it
            lets you start/stop/restart the game service without rebooting the VM and
            auto-shuts the VM down when idle. Installation requires SSH / cloud-init access
            to the server.
          </Alert>
          <Box>
            <Button
              variant="contained"
              onClick={mintProvisioning}
              disabled={pending}
            >
              Enable agent
            </Button>
          </Box>
          {provisioningToken && (
            <Alert severity="success">
              <Typography variant="body2">
                Agent enabled. Copy this one-time install token now — it will not be shown
                again and expires in 24&nbsp;h.
              </Typography>
              <Box component="pre" sx={{
                mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1,
                overflowX: 'auto', fontFamily: 'monospace', fontSize: '0.85rem',
              }}>
                {provisioningToken}
              </Box>
              <ProvisioningTokenHelp
                baseUrl={baseUrl}
                token={provisioningToken}
                guildId={guildId}
                instanceNickname={instanceNickname}
                plugin={String(status.plugin || 'minecraft')}
              />
            </Alert>
          )}
        </Stack>
      )}

      {status && status.agent_enabled && (
        <Stack spacing={2}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Registered: {status.agent_registered ? 'yes' : 'no — awaiting install'} • Last seen:{' '}
              {formatRelative(status.agent_last_seen)}
              {lastState?.event ? ` • Last event: ${lastState.event}` : ''}
            </Typography>
            {lastState?.data && (
              <Typography variant="caption" component="pre" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(lastState.data, null, 2)}
              </Typography>
            )}
          </Box>

          {!status.agent_registered && (
            <Alert severity="info">
              <AgentVerifySteps />
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  void refresh();
                }}
                disabled={loading || pending}
                sx={{ mt: 1.5 }}
              >
                Refresh status
              </Button>
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Runtime behavior (Saga’s copy)
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
              These values are what Saga has stored (e.g. from the last <code>/idle-config</code> in Discord
              or install defaults). The <strong>process on the game server</strong> applies{' '}
              <code>/etc/saga-agent/config.yml</code> for idle timing and the plugin. Edit that file and run{' '}
              <code>systemctl restart saga-agent</code> to change real behavior. A future version may sync
              this UI to the agent automatically.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'block', my: 0.5 }}>
              <strong>Plugin:</strong> {status.plugin || '—'} · <strong>Idle shutdown:</strong>{' '}
              {status.idle_shutdown_enabled ? 'on' : 'off'} · <strong>Idle minutes:</strong>{' '}
              {status.idle_shutdown_minutes} · <strong>Cloud stop via Saga:</strong>{' '}
              {status.cloud_stop_allowed ? 'allowed' : 'not allowed (local shutdown only)'}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="body2" sx={{ minWidth: 120 }}>Service:</Typography>
            <ButtonGroup size="small" disabled={pending || !status.agent_registered}>
              <Button onClick={() => enqueue('start')}>Start</Button>
              <Button onClick={() => enqueue('stop')}>Stop</Button>
              <Button onClick={() => enqueue('restart')}>Restart</Button>
            </ButtonGroup>
            {!status.agent_registered && (
              <Tooltip title="Agent has not registered yet for this instance.">
                <Typography variant="caption" color="text.secondary">
                  commands disabled
                </Typography>
              </Tooltip>
            )}
          </Stack>

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Button variant="outlined" size="small" onClick={mintProvisioning} disabled={pending}>
              {status.agent_registered ? 'Rotate install token' : 'Re-issue install token'}
            </Button>
            {status.agent_registered && (
              <Tooltip title="Invalidate the current refresh token only. Keeps the SQS queue and config. Use this if you suspect the refresh token on disk was compromised.">
                <span>
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    onClick={revokeRefreshToken}
                    disabled={pending}
                  >
                    Revoke refresh token
                  </Button>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Deletes the SQS queue and revokes the current refresh token. You can re-enable later.">
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={disableAgent}
                  disabled={pending}
                >
                  Disable agent
                </Button>
              </span>
            </Tooltip>
          </Stack>

          {provisioningToken && (
            <Alert severity="info">
              <Typography variant="body2">
                Copy this token now. It will not be shown again and expires in 24&nbsp;h.
              </Typography>
              <Box component="pre" sx={{
                mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1,
                overflowX: 'auto', fontFamily: 'monospace', fontSize: '0.85rem',
              }}>
                {provisioningToken}
              </Box>
              <ProvisioningTokenHelp
                baseUrl={baseUrl}
                token={provisioningToken}
                guildId={guildId}
                instanceNickname={instanceNickname}
                plugin={String(status.plugin || 'minecraft')}
              />
            </Alert>
          )}
        </Stack>
      )}
    </Paper>
  );
};
